"use client";

import { createContext, useContext, type MutableRefObject } from "react";
import type { AttachedFileView } from "@cabal/shared/domain/upload/types.js";
import type { ChatMode } from "@/types/frontend-types";

/** Sections of the Session Settings modal that can be deep-linked / scrolled to. */
export enum SessionSettingsSection {
  CurrentTeam = "currentTeam",
}

export interface OpenSessionSettingsOptions {
  /** When set, the modal scrolls to this section once it has opened. */
  section?: SessionSettingsSection;
}

export interface OpenFilePreviewInput {
  files: AttachedFileView[];
  initialFileId: string;
}

export interface AfterCollabReferenceNavigateInput {
  chatMode: ChatMode;
  messageId: string;
  productId: string | null;
}

export type AfterCollabReferenceNavigateHandler = (
  input: AfterCollabReferenceNavigateInput,
) => void;

export interface OpenMemberGlimpseInput {
  memberId: string;
  /** The clicked pill/button rect — the glimpse morph origin (null/omitted = centre grow). */
  originRect?: DOMRect | null;
  /** Stacking offset when the glimpse opens above another modal (e.g. product timeline). */
  modalIndex?: number;
  /** Element to restore focus to when the glimpse closes. */
  restoreFocusTo?: HTMLElement | null;
  /** Invoked when the glimpse closes — lets an opener with its own state stay in sync. */
  onClose?: () => void;
  /**
   * Re-open as the SAME owner (in-place update: new member/stacking, no close notification).
   * Without it, opening over a live glimpse notifies and replaces the previous owner.
   */
  reuseOwnerToken?: MemberGlimpseOwnerToken;
}

/** Identifies which opener owns the current glimpse; scoped closes ignore stale owners. */
export type MemberGlimpseOwnerToken = number;

export interface SessionUIContextValue {
  beforeReferenceNavigateRef: MutableRefObject<(() => void) | null>;
  setBeforeNavigate: (handler: (() => void) | null) => void;
  afterCollabReferenceNavigateRef: MutableRefObject<AfterCollabReferenceNavigateHandler | null>;
  setAfterCollabNavigate: (
    handler: AfterCollabReferenceNavigateHandler | null,
  ) => void;
  /** Trigger: open the session-scoped file preview modal. */
  openFilePreview: (input: OpenFilePreviewInput) => void;
  /** Registration: the session page supplies the concrete file preview handler. */
  setOpenFilePreview: (
    handler: ((input: OpenFilePreviewInput) => void) | null,
  ) => void;
  /** Trigger: open the Session Settings modal (optionally scrolled to a section). */
  openSessionSettings: (options?: OpenSessionSettingsOptions) => void;
  /** Registration: the modal owner supplies the concrete open handler here. */
  setOpenSessionSettings: (
    handler: ((options?: OpenSessionSettingsOptions) => void) | null,
  ) => void;
  /** Trigger: open the Time Machine modal from any session control. */
  openTimeMachine: () => void;
  /** Registration: the Time Machine owner supplies the concrete open handler here. */
  setOpenTimeMachine: (handler: (() => void) | null) => void;
  /** Trigger: open the shared member glimpse host for a session member. */
  openMemberGlimpse: (input: OpenMemberGlimpseInput) => MemberGlimpseOwnerToken;
  /**
   * Trigger: close the shared member glimpse. With a token, closes only while that
   * opener still owns the glimpse (a superseded opener's close is a no-op).
   */
  closeMemberGlimpse: (ownerToken?: MemberGlimpseOwnerToken) => void;
}

export const SessionUIContext = createContext<SessionUIContextValue | null>(
  null,
);

export function useSessionUIContext(): SessionUIContextValue {
  const context = useContext(SessionUIContext);
  if (!context) {
    throw new Error(
      "useSessionUIContext must be used within SessionUIProvider",
    );
  }
  return context;
}
